import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useColorScheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import DictIconBadge from './DictIconBadge';
import {
  CUSTOM_COLOR_CHOICES,
  CUSTOM_ICON_CHOICES,
  resolveCustomIcon,
} from './customIcons';

interface ICreateDictionaryModalProps {
  open: boolean;
  onClose: () => void;
}

const FIELD_LABEL_SX = {
  color: 'text.secondary',
  fontSize: 13,
  fontWeight: 700,
  mb: 1.125,
} as const;

const CreateDictionaryModal: React.FC<ICreateDictionaryModalProps> = ({
  open,
  onClose,
}) => {
  const { mode } = useColorScheme();
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('work');
  const [accent, setAccent] = useState('#488BFF');

  const PreviewIcon = resolveCustomIcon(icon);
  const previewTint = mode === 'dark' ? 0.12 : 0.06;
  const previewBorder = alpha(accent, 0.35);

  // Custom dictionaries aren't wired to the BE yet — the modal acts as a
  // visual stub: it validates the form and closes, no persistence.
  const submit = () => {
    if (!name.trim()) return;
    onClose();
    setName('');
    setIcon('work');
    setAccent('#488BFF');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableScrollLock
      maxWidth={'xs'}
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
    >
      <DialogTitle
        sx={{
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          fontSize: 20,
          fontWeight: 800,
          gap: 1.5,
          px: 2.5,
          py: 2.25,
        }}
      >
        <Box sx={{ flex: 1 }}>{'Новий словник'}</Box>
        <IconButton
          aria-label={'Закрити'}
          onClick={onClose}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: '20px !important' }}>
        <Stack spacing={2.75}>
          {/* Live preview */}
          <Stack
            direction={'row'}
            alignItems={'center'}
            spacing={1.75}
            sx={{
              bgcolor: alpha(accent, previewTint),
              border: `1px solid ${previewBorder}`,
              borderRadius: 1.5,
              p: '14px 16px',
            }}
          >
            <DictIconBadge Icon={PreviewIcon} accent={accent} size={46} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: name ? 'text.primary' : 'text.secondary',
                  fontSize: 17,
                  fontWeight: 700,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {name || 'Назва словника'}
              </Typography>
              <Typography
                sx={{ color: 'text.secondary', fontSize: 13, mt: 0.25 }}
              >
                {'Порожній — почни додавати слова'}
              </Typography>
            </Box>
          </Stack>

          {/* Name */}
          <Box>
            <Typography sx={FIELD_LABEL_SX}>{'Назва'}</Typography>
            <TextField
              fullWidth
              value={name}
              placeholder={'Напр., Бізнес-англійська'}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </Box>

          {/* Icon picker */}
          <Box>
            <Typography sx={FIELD_LABEL_SX}>{'Іконка'}</Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                gridTemplateColumns: 'repeat(6, 1fr)',
              }}
            >
              {CUSTOM_ICON_CHOICES.map(({ key, Icon }) => {
                const on = key === icon;
                return (
                  <Box
                    key={key}
                    component={'button'}
                    type={'button'}
                    onClick={() => setIcon(key)}
                    aria-label={key}
                    sx={{
                      alignItems: 'center',
                      bgcolor: on
                        ? alpha(accent, mode === 'dark' ? 0.22 : 0.12)
                        : 'background.paper',
                      border: 1,
                      borderColor: on ? accent : 'primary.tableBorder',
                      borderRadius: 1.25,
                      color: on ? accent : 'text.secondary',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      height: 46,
                      justifyContent: 'center',
                      transition: 'all .14s ease',
                    }}
                  >
                    <Icon sx={{ fontSize: 22 }} />
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Color picker */}
          <Box>
            <Typography sx={FIELD_LABEL_SX}>{'Колір'}</Typography>
            <Stack direction={'row'} flexWrap={'wrap'} spacing={1.5}>
              {CUSTOM_COLOR_CHOICES.map((c) => {
                const on = c === accent;
                return (
                  <Box
                    key={c}
                    component={'button'}
                    type={'button'}
                    onClick={() => setAccent(c)}
                    aria-label={c}
                    sx={(theme) => ({
                      bgcolor: c,
                      border: 'none',
                      borderRadius: '50%',
                      boxShadow: on
                        ? `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${c}`
                        : 'none',
                      cursor: 'pointer',
                      height: 30,
                      transition: 'box-shadow .14s ease',
                      width: 30,
                    })}
                  />
                );
              })}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          gap: 1.25,
          px: 2.5,
          py: 2,
        }}
      >
        <Button variant={'text'} onClick={onClose}>
          {'Скасувати'}
        </Button>
        <Button
          disabled={!name.trim()}
          onClick={submit}
          startIcon={<AddIcon sx={{ fontSize: 18 }} />}
        >
          {'Створити словник'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateDictionaryModal;
